import React from 'react';
import { motion } from 'framer-motion';

// Scroll reveal animation variants
const variants = {
    fadeUp: {
        hidden: { opacity: 0, y: 60 },
        visible: { opacity: 1, y: 0 }
    },
    fadeDown: {
        hidden: { opacity: 0, y: -60 },
        visible: { opacity: 1, y: 0 }
    },
    fadeLeft: {
        hidden: { opacity: 0, x: -60 },
        visible: { opacity: 1, x: 0 }
    },
    fadeRight: {
        hidden: { opacity: 0, x: 60 },
        visible: { opacity: 1, x: 0 }
    },
    fadeIn: {
        hidden: { opacity: 0 },
        visible: { opacity: 1 }
    },
    scaleUp: {
        hidden: { opacity: 0, scale: 0.8 },
        visible: { opacity: 1, scale: 1 }
    },
    rotateIn: {
        hidden: { opacity: 0, rotate: -10, scale: 0.9 },
        visible: { opacity: 1, rotate: 0, scale: 1 }
    }
};

/**
 * ScrollReveal - A reusable component for scroll-triggered animations
 * 
 * @param {string} animation - Animation type: 'fadeUp', 'fadeDown', 'fadeLeft', 'fadeRight', 'fadeIn', 'scaleUp', 'rotateIn'
 * @param {number} delay - Delay in seconds before animation starts
 * @param {number} duration - Duration of the animation in seconds
 * @param {boolean} once - If true, animation only plays once
 * @param {string} className - Additional CSS classes
 * @param {React.ReactNode} children - Child elements to animate
 */
const ScrollReveal = ({
    children,
    animation = 'fadeUp',
    delay = 0,
    duration = 0.6,
    once = true,
    className = '',
    threshold = 0.1,
    ...props
}) => {
    return (
        <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once, amount: threshold }}
            variants={variants[animation] || variants.fadeUp}
            transition={{
                duration,
                delay,
                ease: [0.25, 0.1, 0.25, 1] // Custom easing for smooth feel
            }}
            className={className}
            {...props}
        >
            {children}
        </motion.div>
    );
};

/**
 * StaggerContainer - Container for staggered child animations
 */
export const StaggerContainer = ({
    children,
    staggerDelay = 0.1,
    className = '',
    once = true,
    ...props
}) => {
    return (
        <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once, amount: 0.1 }}
            variants={{
                hidden: {},
                visible: {
                    transition: {
                        staggerChildren: staggerDelay
                    }
                }
            }}
            className={className}
            {...props}
        >
            {children}
        </motion.div>
    );
};

/**
 * StaggerItem - Child item for StaggerContainer
 */
export const StaggerItem = ({
    children,
    animation = 'fadeUp',
    className = '',
    ...props
}) => {
    return (
        <motion.div
            variants={variants[animation] || variants.fadeUp}
            transition={{
                duration: 0.5,
                ease: [0.25, 0.1, 0.25, 1]
            }}
            className={className}
            {...props}
        >
            {children}
        </motion.div>
    );
};

export default ScrollReveal;
